/**
 * @license
 * Copyright (c) 2021 Handsoncode. All rights reserved.
 */
import { AbsoluteCellRange } from '../AbsoluteCellRange';
import { CellError } from '../Cell';
import { ErroredMatrix, Matrix, MatrixSize, NotComputedMatrix } from '../Matrix';
import { ColumnsSpan, RowsSpan } from '../Span';
export class MatrixVertex {
    constructor(cellAddress, width, height, formula) {
        this.cellAddress = cellAddress;
        this.formula = formula || null;
        this.matrix = new NotComputedMatrix(new MatrixSize(width, height));
    }
    get width() {
        return this.matrix.width();
    }
    get height() {
        return this.matrix.height();
    }
    get sheet() {
        return this.cellAddress.sheet;
    }
    static fromRange(range, formula) {
        return new MatrixVertex(range.start, range.width(), range.height(), formula);
    }
    setCellValue(matrix) {
        this.matrix = matrix;
    }
    setErrorValue(error) {
        this.matrix = new ErroredMatrix(error, this.matrix.size);
    }
    getCellValue() {
        if (this.matrix instanceof NotComputedMatrix) {
            throw Error('Matrix not computed yet.');
        }
        return this.matrix;
    }
    getMatrixCellValue(address) {
        const col = address.col - this.cellAddress.col;
        const row = address.row - this.cellAddress.row;
        return this.matrix.get(col, row);
    }
    getMatrixCellRawValue(address) {
        const val = this.getMatrixCellValue(address);
        return val instanceof CellError ? undefined : val;
    }
    setMatrixCellValue(address, value) {
        const col = address.col - this.cellAddress.col;
        const row = address.row - this.cellAddress.row;
        if (this.matrix instanceof Matrix) {
            this.matrix.set(col, row, value);
        }
    }
    getRange() {
        return AbsoluteCellRange.spanFrom(this.cellAddress, this.width, this.height);
    }
    getAddress() {
        return this.cellAddress;
    }
    setAddress(address) {
        this.cellAddress = address;
    }
    getFormula() {
        return this.formula;
    }
    setFormula(newFormula) {
        this.formula = newFormula;
    }
    isFormula() {
        return this.formula !== null;
    }
    isNumeric() {
        return this.formula === null;
    }
    spansThroughSheetRows(sheet, startRow, endRow = startRow) {
        return (this.cellAddress.sheet === sheet) &&
            (this.cellAddress.row <= endRow) &&
            (startRow < this.cellAddress.row + this.height);
    }
    spansThroughSheetColumn(sheet, col, columnEnd = col) {
        return (this.cellAddress.sheet === sheet) &&
            (this.cellAddress.col <= columnEnd) &&
            (col < this.cellAddress.col + this.width);
    }
    addRows(sheet, row, numberOfRows) {
        if (this.matrix instanceof Matrix) {
            this.matrix.addRows(row - this.getAddress().row, numberOfRows);
        }
    }
    addColumns(sheet, column, numberOfColumns) {
        if (this.matrix instanceof Matrix) {
            this.matrix.addColumns(column - this.getAddress().col, numberOfColumns);
        }
    }
    removeRows(removedRows) {
        if (this.matrix instanceof Matrix) {
            const removedRowsFromMatrix = this.rowsFromMatrix().intersect(removedRows);
            this.matrix.removeRows(removedRowsFromMatrix.rowStart - this.getAddress().row, removedRowsFromMatrix.rowEnd - this.getAddress().row);
        }
    }
    removeColumns(removedColumns) {
        if (this.matrix instanceof Matrix) {
            const removedColumnsFromMatrix = this.columnsFromMatrix().intersect(removedColumns);
            this.matrix.removeColumns(removedColumnsFromMatrix.columnStart - this.getAddress().col, removedColumnsFromMatrix.columnEnd - this.getAddress().col);
        }
    }
    isComputed() {
        return (!(this.matrix instanceof NotComputedMatrix));
    }
    columnsFromMatrix() {
        return ColumnsSpan.fromNumberOfColumns(this.cellAddress.sheet, this.cellAddress.col, this.width);
    }
    rowsFromMatrix() {
        return RowsSpan.fromNumberOfRows(this.cellAddress.sheet, this.cellAddress.row, this.height);
    }
}
